import {
  HeartIcon,
  UsersIcon,
  GlobeAltIcon,
  BuildingStorefrontIcon,
  TruckIcon,
  ChartBarIcon,
} from "@heroicons/react/24/outline";
import ImpactCard from "../components/ImpactCard";
import { useEffect, useState } from "react";
import { supabase } from "../supabase";
// NEW: Import from Recharts
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from "recharts";
import { Link } from "react-router-dom";

export default function Impact() {
  const [impactStats, setImpactStats] = useState({
    co2Saved: 0,
    peopleReached: 0,
    totalBusinessesJoined: 0,
    totalFoodWasteSaved: 0,
    totalMealsShared: 0,
    totalUsersActive: 0,
  });

  useEffect(() => {
    async function fetchStats() {
      try {
        const { data, error } = await supabase
          .from("stats")
          .select("*")
          .eq("type", "impact")
          .single();

        if (error) throw error;
        if (data) {
          setImpactStats((prev) => ({
            ...prev,
            co2Saved: data.co2_saved || 0,
            peopleReached: data.people_reached || 0,
            totalBusinessesJoined: data.total_businesses_joined || 0,
            totalFoodWasteSaved: data.total_food_waste_saved || 0,
            totalMealsShared: data.total_meals_shared || 0,
            totalUsersActive: data.total_users_active || 0,
          }));
        }
      } catch (e) {
        // fallback: keep zeroes
      }
    }
    fetchStats();
  }, []);

  const environmentalImpact = [
    {
      label: "CO₂ Emissions Reduced",
      value: `${impactStats.co2Saved.toLocaleString()} kg`,
      description: "Equivalent to planting 205 trees",
    },
    {
      label: "Water Saved",
      value: "12,847 L",
      description: "Through reduced food production needs",
    },
    {
      label: "Landfill Waste Prevented",
      value: `${impactStats.totalFoodWasteSaved.toLocaleString()} kg`,
      description: "Food diverted from waste streams",
    },
  ];

  const socialImpact = [
    {
      label: "People Reached",
      value: impactStats.peopleReached.toLocaleString(),
      description: "Regular access to nutritious meals",
    },
    {
      label: "Children Fed",
      value: "5,678",
      description: "Young lives positively impacted",
    },
    {
      label: "Elderly Assisted",
      value: "1,234",
      description: "Senior citizens supported",
    },
  ];

  // Chart data
  const monthlyData = [
    { month: "Jan", meals: 980, waste: 650 },
    { month: "Feb", meals: 1240, waste: 820 },
    { month: "Mar", meals: 1560, waste: 1030 },
    { month: "Apr", meals: 1890, waste: 1250 },
    { month: "May", meals: 2100, waste: 1390 },
    { month: "Jun", meals: 2340, waste: 1550 },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-emerald-50/20">
      {/* Hero Section */}
      <div className="py-16 text-white bg-gradient-to-br from-emerald-600 via-teal-700 to-cyan-600">
        <div className="px-4 mx-auto text-center max-w-7xl sm:px-6 lg:px-8">
          <h1 className="mb-3 text-2xl font-bold md:text-3xl lg:text-4xl">
            Our Collective Impact
          </h1>
          <p className="max-w-2xl mx-auto text-base text-white/95 font-medium md:text-lg">
            Together, we're transforming surplus food into hope, reducing waste,
            and building stronger communities across Sri Lanka.
          </p>
        </div>
      </div>

      <div className="px-4 py-12 mx-auto max-w-7xl sm:px-6 lg:px-8">
        {/* Main Impact Stats */}
        <div className="grid grid-cols-1 gap-6 mb-12 md:grid-cols-2 lg:grid-cols-4">
          <ImpactCard
            title="Total Meals Shared"
            value={impactStats.totalMealsShared.toLocaleString()}
            subtitle="Nutritious meals distributed"
            icon={<HeartIcon className="w-4 h-4" />}
            color="primary"
            trend={{ value: 23, label: "vs last month" }}
          />
          <ImpactCard
            title="Active Community Members"
            value={impactStats.totalUsersActive.toLocaleString()}
            subtitle="Donors, recipients & volunteers"
            icon={<UsersIcon className="w-4 h-4" />}
            color="accent"
            trend={{ value: 15, label: "vs last month" }}
          />
          <ImpactCard
            title="Food Waste Prevented"
            value={`${impactStats.totalFoodWasteSaved.toLocaleString()} kg`}
            subtitle="Diverted from landfills"
            icon={<GlobeAltIcon className="w-4 h-4" />}
            color="secondary"
            trend={{ value: 31, label: "vs last month" }}
          />
          <ImpactCard
            title="Partner Businesses"
            value={impactStats.totalBusinessesJoined.toLocaleString()}
            subtitle="Restaurants, bakeries & hotels"
            icon={<BuildingStorefrontIcon className="w-4 h-4" />}
            color="primary"
            trend={{ value: 8, label: "vs last month" }}
          />
        </div>

        {/* Environmental Impact */}
        <section className="mb-12">
          <div className="mb-8 text-center">
            <h2 className="mb-3 text-2xl font-bold text-slate-900 lg:text-3xl">
              Environmental Impact
            </h2>
            <p className="max-w-2xl mx-auto text-base text-slate-600 font-medium">
              Every meal shared contributes to a more sustainable future for Sri
              Lanka
            </p>
          </div>
          <div className="grid grid-cols-1 gap-6 mb-8 md:grid-cols-3">
            {environmentalImpact.map((item, index) => (
              <div key={index} className="text-center card-floating">
                <div className="flex items-center justify-center w-10 h-10 mx-auto mb-3 bg-gradient-to-br from-emerald-100 to-teal-100 rounded-lg shadow-lg">
                  <GlobeAltIcon className="w-5 h-5 text-emerald-600" />
                </div>
                <h3 className="mb-2 text-xl font-bold text-slate-900">
                  {item.value}
                </h3>
                <p className="mb-2 text-sm font-bold text-slate-900">
                  {item.label}
                </p>
                <p className="text-sm text-slate-600 font-medium">
                  {item.description}
                </p>
              </div>
            ))}
          </div>

          <div className="p-6 border-2 border-emerald-200 rounded-xl bg-gradient-to-br from-emerald-50 to-teal-50 shadow-xl">
            <div className="flex items-center mb-3">
              <GlobeAltIcon className="w-5 h-5 mr-2 text-emerald-600" />
              <h3 className="text-lg font-bold text-emerald-900">
                Carbon Footprint Reduction
              </h3>
            </div>
            <p className="mb-6 text-sm text-emerald-800 font-medium">
              By redistributing surplus food instead of letting it go to waste,
              MealBridge has prevented the equivalent of
              <span className="font-bold text-lg">
                {" "}
                {impactStats.co2Saved
                  ? impactStats.co2Saved.toLocaleString()
                  : "0"}{" "}
                kg of CO₂
              </span>{" "}
              from entering the atmosphere. This is equivalent to:
            </p>
            <div className="grid grid-cols-1 gap-4 text-sm text-emerald-700 md:grid-cols-3">
              <div className="flex items-center">
                <span className="w-3 h-3 mr-3 bg-emerald-500 rounded-full"></span>
                <span className="font-bold">
                  205 trees planted and grown for 10 years
                </span>
              </div>
              <div className="flex items-center">
                <span className="w-3 h-3 mr-3 bg-emerald-500 rounded-full"></span>
                <span className="font-bold">
                  19,800 km driven by an average car
                </span>
              </div>
              <div className="flex items-center">
                <span className="w-3 h-3 mr-3 bg-emerald-500 rounded-full"></span>
                <span className="font-bold">
                  2.1 tons of waste recycled instead of landfilled
                </span>
              </div>
            </div>
          </div>
        </section>

        {/* Social Impact */}
        <section className="mb-12">
          <div className="mb-8 text-center">
            <h2 className="mb-3 text-2xl font-bold text-slate-900 lg:text-3xl">
              Social Impact
            </h2>
            <p className="max-w-2xl mx-auto text-base text-slate-600 font-medium">
              Building stronger, more connected communities through food sharing
            </p>
          </div>
          <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
            {socialImpact.map((item, index) => (
              <div key={index} className="text-center card-floating">
                <div className="flex items-center justify-center w-10 h-10 mx-auto mb-3 bg-gradient-to-br from-cyan-100 to-blue-100 rounded-lg shadow-lg">
                  <UsersIcon className="w-5 h-5 text-cyan-600" />
                </div>
                <h3 className="mb-2 text-xl font-bold text-slate-900">
                  {item.value}
                </h3>
                <p className="mb-2 text-sm font-bold text-slate-900">
                  {item.label}
                </p>
                <p className="text-sm text-slate-600 font-medium">
                  {item.description}
                </p>
              </div>
            ))}
          </div>
        </section>

        {/* Growth Chart (now a line chart) */}
        <section className="mb-12">
          <div className="card-floating">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="mb-2 text-xl font-bold text-slate-900 lg:text-2xl">
                  Growth Over Time
                </h2>
                <p className="text-sm text-slate-600 font-medium">
                  Monthly meals shared and waste prevented
                </p>
              </div>
              <ChartBarIcon className="w-5 h-5 text-emerald-600" />
            </div>
            {/* --- Begin Line Chart --- */}
            <div className="h-[300px] w-full">
              <ResponsiveContainer width="100%" height={300}>
                <LineChart
                  data={monthlyData}
                  margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="month" stroke="#64748b" fontSize={14} />
                  <YAxis stroke="#64748b" fontSize={14} />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: "white",
                      border: "2px solid #e2e8f0",
                      borderRadius: "12px",
                      boxShadow: "0 10px 25px rgba(0,0,0,0.1)",
                    }}
                  />
                  <Legend />
                  <Line
                    type="monotone"
                    dataKey="meals"
                    name="Meals Shared"
                    stroke="#10b981"
                    strokeWidth={4}
                    dot={{ r: 6, fill: "#10b981" }}
                  />
                  <Line
                    type="monotone"
                    dataKey="waste"
                    name="Waste Prevented (kg)"
                    stroke="#f59e0b"
                    strokeWidth={4}
                    dot={{ r: 6, fill: "#f59e0b" }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
            {/* --- End Line Chart --- */}
          </div>
        </section>

        {/* Volunteer Impact */}
        <section className="mb-12">
          <div className="p-8 border-2 border-amber-200 rounded-xl bg-gradient-to-br from-amber-50 to-orange-50 shadow-xl">
            <div className="text-center">
              <TruckIcon className="w-10 h-10 mx-auto mb-3 text-amber-600" />
              <h2 className="mb-6 text-xl font-bold text-slate-900 lg:text-2xl">
                Volunteer Network Impact
              </h2>
              <div className="grid grid-cols-1 gap-6 mb-8 md:grid-cols-3">
                <div>
                  <p className="text-2xl font-bold text-amber-600">1,247</p>
                  <p className="text-sm text-slate-700 font-bold">
                    Deliveries Completed
                  </p>
                </div>
                <div>
                  <p className="text-2xl font-bold text-amber-600">3,456</p>
                  <p className="text-sm text-slate-700 font-bold">
                    Volunteer Hours
                  </p>
                </div>
                <div>
                  <p className="text-2xl font-bold text-amber-600">89%</p>
                  <p className="text-sm text-slate-700 font-bold">
                    On-Time Delivery Rate
                  </p>
                </div>
              </div>
              <p className="max-w-2xl mx-auto text-sm text-slate-700 font-medium">
                Our dedicated volunteer network has made it possible to reach
                communities across all 25 districts of Sri Lanka, ensuring that
                surplus food reaches those who need it most, when they need it.
              </p>
            </div>
          </div>
        </section>

        {/* Call to Action */}
        <section className="text-center">
          <div className="card-floating bg-gradient-to-r from-emerald-50 to-teal-50 border-2 border-emerald-200">
            <h2 className="mb-4 text-xl font-bold text-slate-900 lg:text-2xl">
              Be Part of the Solution
            </h2>
            <p className="max-w-2xl mx-auto mb-8 text-sm text-slate-700 font-medium">
              Every donation, every claim, and every volunteer hour contributes
              to these incredible numbers. Join us in creating an even bigger
              impact for Sri Lanka's communities.
            </p>
            <div className="flex flex-col justify-center gap-4 sm:flex-row">
              <Link
                to="/auth"
                className="px-6 py-3 text-sm font-bold text-white transition-all duration-500 transform bg-gradient-to-r from-emerald-500 via-teal-500 to-cyan-500 rounded-xl shadow-xl hover:shadow-2xl hover:-translate-y-1 hover:scale-105"
              >
                Start Donating
              </Link>
              <Link
                to="/auth"
                className="px-6 py-3 text-sm font-bold text-slate-600 transition-all duration-500 transform bg-slate-100 rounded-xl shadow-xl hover:shadow-2xl hover:-translate-y-1 hover:scale-105 hover:bg-slate-200"
              >
                Become a Volunteer
              </Link>
              <button className="px-6 py-3 text-sm font-bold text-slate-600 transition-all duration-500 transform border-2 border-slate-200 rounded-xl shadow-xl hover:shadow-2xl hover:-translate-y-1 hover:scale-105 hover:bg-slate-50">
                Share Our Impact
              </button>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}
